import { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Flame, Target, Check, Clock, ExternalLink, ChevronUp, ChevronDown, BellOff } from "lucide-react";
import { ReminderOffForm } from "@/components/LeadReminders";
import { LeaveNudge } from "@/components/LeaveNudge";
import { toast } from "sonner";
import api, { formatApiError } from "@/api/client";
import { todayIstIso } from "@/utils/date";

const fmt = (iso) => { const [y, m, d] = iso.split("-"); return `${d}/${m}/${y.slice(2)}`; };
const daysLate = (iso) => Math.round((new Date(`${todayIstIso()}T00:00:00`) - new Date(`${iso}T00:00:00`)) / 864e5);

function LeadNudge({ lead, onChanged }) {
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [off, setOff] = useState(false);
  const [note, setNote] = useState("");
  const [next, setNext] = useState("");
  const late = daysLate(lead.next_action_date);
  const done = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/hot-leads/${lead.id}/done`, { note, next_action_date: next || null });
      toast.success("Next step logged"); onChanged();
    } catch (x) { toast.error(formatApiError(x?.response?.data?.detail) || "Failed"); }
  };
  const later = async () => {
    try { await api.post(`/hot-leads/${lead.id}/snooze`); onChanged(); } catch { toast.error("Failed"); }
  };
  return (
    <div className="rounded-xl border border-orange-500/40 bg-surface shadow-float p-3 animate-fade-in-up" data-testid={`nudge-lead-${lead.id}`}>
      <div className="flex items-start gap-2">
        <Flame size={16} className="text-orange-500 mt-0.5 shrink-0" />
        <div className="min-w-0 flex-1">
          <div className="client-line text-sm truncate">{lead.client}</div>
          <div className="text-xs text-white/70 truncate">{lead.project_title}</div>
          <div className={`text-xs mt-0.5 ${late > 0 ? "text-red-600 dark:text-red-400 font-semibold" : "text-orange-600 dark:text-orange-400"}`}>
            Next action {late > 0 ? `overdue by ${late} day${late > 1 ? "s" : ""}` : "due today"} · {fmt(lead.next_action_date)}
          </div>
        </div>
      </div>
      {off ? (
        <ReminderOffForm leadId={lead.id} onDone={onChanged} onCancel={() => setOff(false)} />
      ) : open ? (
        <form onSubmit={done} className="mt-2 space-y-2">
          <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} required placeholder="What was done?" data-testid={`nudge-lead-note-${lead.id}`}
            className="w-full resize-none rounded-md border border-white/15 bg-transparent px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#E11D2E]/30" />
          <label className="text-xs text-white/60 flex items-center gap-2">Next action date
            <input type="date" value={next} min={todayIstIso()} onChange={(e) => setNext(e.target.value)} data-testid={`nudge-lead-next-${lead.id}`}
              className="h-8 rounded-md border border-white/15 bg-transparent px-2 text-xs text-white" />
          </label>
          <div className="flex gap-2">
            <button type="submit" disabled={note.trim().length < 2} data-testid={`nudge-lead-save-${lead.id}`} className="h-8 px-3 rounded-md bg-[#E11D2E] hover:bg-[#F03041] text-white text-xs font-semibold disabled:opacity-50">Save</button>
            <button type="button" onClick={() => setOpen(false)} className="h-8 px-3 rounded-md border border-white/15 text-xs">Back</button>
          </div>
        </form>
      ) : (
        <div className="flex gap-1.5 mt-2">
          <button type="button" onClick={() => setOpen(true)} data-testid={`nudge-lead-done-${lead.id}`} className="h-8 px-2.5 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold inline-flex items-center gap-1"><Check size={13} /> Done</button>
          <button type="button" onClick={later} data-testid={`nudge-lead-later-${lead.id}`} title="Hide for 2 hours" className="h-8 px-2.5 rounded-md border border-white/15 text-xs inline-flex items-center gap-1 hover:bg-white/5"><Clock size={13} /> Remind me later</button>
          <button type="button" onClick={() => setOff(true)} data-testid={`nudge-lead-off-${lead.id}`} title="Don't remind for this lead" className="h-8 w-8 grid place-items-center rounded-md border border-white/15 hover:bg-white/5"><BellOff size={13} /></button>
          <button type="button" onClick={() => nav("/hot-leads")} data-testid={`nudge-lead-open-${lead.id}`} title="Open Hot Leads" className="h-8 w-8 grid place-items-center rounded-md border border-white/15 hover:bg-white/5"><ExternalLink size={13} /></button>
        </div>
      )}
    </div>
  );
}

// Floating reminders: due hot-lead next actions + pending weekly target logs.
export const FloatingNudges = () => {
  const nav = useNavigate();
  const { pathname } = useLocation();
  const [leads, setLeads] = useState([]);
  const [leaves, setLeaves] = useState([]);
  const [week, setWeek] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const load = useCallback(async () => {
    const [l, w, lv] = await Promise.allSettled([api.get("/hot-leads/due"), api.get("/me/target-nudges"), api.get("/leaves/nudges")]);
    setLeaves(lv.status === "fulfilled" ? lv.value.data : []);
    setLeads(l.status === "fulfilled" ? l.value.data : []);
    setWeek(w.status === "fulfilled" ? w.value.data : null);
  }, []);
  useEffect(() => { load(); const t = setInterval(load, 180000); return () => clearInterval(t); }, [load, pathname]);
  useEffect(() => { window.addEventListener("lim:nudges-refresh", load); return () => window.removeEventListener("lim:nudges-refresh", load); }, [load]);

  const pendingTeams = week?.pending || [];
  const total = leads.length + pendingTeams.length + leaves.length;
  if (!total) return null;
  const shownLeaves = expanded ? leaves : leaves.slice(0, 1);
  const shownLeads = expanded ? leads : leads.slice(0, Math.max(0, 2 - pendingTeams.length - shownLeaves.length));
  const hidden = leads.length - shownLeads.length + leaves.length - shownLeaves.length;
  return (
    <div className="fixed z-[45] right-4 bottom-[13.5rem] lg:right-20 lg:bottom-24 w-[92vw] sm:w-[340px] space-y-2 max-h-[70vh] overflow-y-auto" data-testid="floating-nudges">
      {pendingTeams.map((team) => (
        <div key={team} className="rounded-xl border border-[#E11D2E]/50 bg-surface shadow-float p-3 animate-fade-in-up" data-testid={`nudge-week-${team.replace(/\W+/g, "-")}`}>
          <div className="flex items-start gap-2">
            <Target size={16} className="text-[#E11D2E] mt-0.5 shrink-0" />
            <div className="min-w-0">
              <div className="text-sm font-semibold">Weekly target log pending</div>
              <div className="text-xs text-white/65">{team} · week {fmt(week.week_start)}–{fmt(week.week_end)}. Confirm what was delivered.</div>
            </div>
          </div>
          <button type="button" onClick={() => nav("/monthly-targets?submit=1")} data-testid="nudge-week-submit"
            className="mt-2 h-8 px-3 rounded-md bg-[#E11D2E] hover:bg-[#F03041] text-white text-xs font-semibold">Submit week</button>
        </div>
      ))}
      {shownLeaves.map((l) => <LeaveNudge key={l.id} leave={l} onChanged={load} />)}
      {shownLeads.map((l) => <LeadNudge key={l.id} lead={l} onChanged={load} />)}
      {hidden > 0 || (expanded && total > 2) ? (
        <button type="button" onClick={() => setExpanded((e) => !e)} data-testid="nudges-toggle"
          className="w-full h-8 rounded-md border border-white/15 bg-surface text-xs font-medium inline-flex items-center justify-center gap-1 shadow-float">
          {expanded ? <><ChevronDown size={13} /> Show fewer</> : <><ChevronUp size={13} /> {hidden} more reminder{hidden > 1 ? "s" : ""}</>}
        </button>
      ) : null}
    </div>
  );
};
